'use strict'

const _ = require('lodash')
const {createResolver} = require('./resolver')
const baseDefinitions = require('./baseDefinitions.json')

// NOTE:: currently preventing using names reserved to base definitions,
// after wix code will add display name to tern.js api we need to remove this and allow 'reserved' names

/**
 * use resolver to resolve property
 * @param (property) property to resolve
 * @param (resolver) wix-json-schema-utils resolver
 * @returns {{propName: *, propID: *, propRef: *, propData: *}}
 */
const resolveProperty = (property, resolver) => {
    const resolvedProp = resolver.resolve(property)
    const propName = _.head(_.keys(resolvedProp))
    return {
        propName,
        propID: resolvedProp[propName].$id,
        propRef: resolvedProp[propName].$ref,
        propData: resolvedProp[propName]
    }
}

/**
 * get name of definition by its $id, find in both base and custom definitions
 * Note: useing name instead of the unique $id to be able to display the name in the code completion
 * @param (defID) string, definition id to find its name
 * @param (customDefinitions) object containing user generated custom definitions (NOTE: needed to prevent conflicts with base definitions)
 * @returns {{propName: string, PropData: Object}}
 */
const getDefinitionNameById = (defID, customDefinitions) =>
    _.findKey(baseDefinitions, ['$id', defID]) || _.findKey(customDefinitions, ['$id', defID]) || defID

/**
 * get object containing !type/!proto or none
 * !type is used when referencing another definition
 * !proto is used when defining a primitive value (string, number, boolean)
 * empty object if data type is objectxw
 * @param (propData) inner object of the property
 * @param (customDefinitions) object containing user generated custom definitions (NOTE: needed to prevent conflicts with base definitions)
 * @returns {{!proto/!type: string}}
 */
const getPropertyTernTypeOrProto = (propData, customDefinitions) => {
    if (_.has(propData, '$ref')) {
        //return {'!type': `+${propData.$ref.substring(1)}`}
        const refID = propData.$ref
        return {'!type': `+${getDefinitionNameById(refID, customDefinitions)}`}
    } else if (propData.type === 'array') {
        const arrType = getPropertyTernTypeOrProto(propData.items, customDefinitions)
        const key = _.head(_.keys(arrType))
        if (!_.isEmpty(key)) {
            arrType[key] = `[${arrType[key]}]`
        }
        return arrType
    } else if (propData.type === 'object') {
        return {}
    }
    return {'!proto': propData.type}
}

/**
 * get property data according to its type
 * @param (propData) inner object of the property
 * @param (customDefinitions) object containing user generated custom definitions (NOTE: needed to prevent conflicts with base definitions)
 * @returns {{!doc: string, !proto/!type: string}}
 */
const propertyTernData = (propData, customDefinitions) => _.assign(
    {'!doc': propData.description},
    getPropertyTernTypeOrProto(propData, customDefinitions)
)

/**
 * convert json-schema property to tern property
 * @param (property) property to convert - not resolved
 * @param (resolver) wix-json-schema-utils resolver
 * @param (customDefinitions) object containing user generated custom definitions (NOTE: needed to prevent conflicts with base definitions)
 * @returns (Object) tern valid data
 */
const propertySchemaToTern = (property, resolver, customDefinitions) => {
    const resolved = resolveProperty(property, resolver)
    //const key = resolved.propID && !resolved.propRef ? resolved.propID : resolved.propName
    const key = resolved.propName
    const tern = {
        [key]: propertyTernData(resolved.propData, customDefinitions)
    }
    if (_.has(resolved.propData, 'properties')) {
        _.forOwn(resolved.propData.properties, (nestedPropData, nestedPropName) => {
            const resolvedNested = resolveProperty({[nestedPropName]: nestedPropData}, resolver)
            tern[key][nestedPropName] = propertyTernData(resolvedNested.propData, customDefinitions)
        })
    }
    return tern
}

/**
 * convert json-schema properties array to tern properties
 * the function receives endless properties bundles (objects or arrays) and merges them together to a big array of properties
 * @param (customDefinitions) object containing user generated definitions
 * @param (...propertiesArgs) arrays or objects of properties to convert - not resolved
 * @returns (Object) object describing all properties
 */
const propertiesSchemaToTern = (customDefinitions, ...propertiesArgs) => {
    let allPropsMerged = []
    for (const propertyArg of propertiesArgs) {
        if (Array.isArray(propertyArg)) {
            allPropsMerged = _.concat(allPropsMerged, propertyArg)
        } else if (typeof propertyArg === 'object') {
            allPropsMerged = _.concat(
                allPropsMerged,
                _.reduce(propertyArg, (acc, data, name) => {
                    acc.push({[name]: data})
                    return acc
                }, [])
            )
        }
    }

    const resolver = createResolver(
        baseDefinitions,
        customDefinitions
    )

    return allPropsMerged
        .map(p => propertySchemaToTern(p, resolver, customDefinitions))
        .reduce((obj, p) => _.assign(obj, p), {})
}

module.exports = {
    propertiesSchemaToTern
}